import { useState, useEffect } from 'react';
import { Badge } from '../../components/ui/badge';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const relLabel = (t) => ({
  blocker: 'Blocker', booker: 'Booker', purchaser: 'Purchaser',
  co_purchaser: 'Co-Purchaser', tenant: 'Tenant', co_tenant: 'Co-Tenant',
  occupying_owner: 'Occupying Owner', interested_party: 'Interested Party',
  co_booker: 'Co-Booker', authorised_representative: 'Auth. Rep',
}[t] || t);

const relColor = (t) => {
  if (t === 'purchaser' || t === 'co_purchaser') return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400';
  if (t === 'booker' || t === 'co_booker') return 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400';
  if (t === 'blocker') return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400';
  if (t === 'tenant' || t === 'co_tenant') return 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400';
  if (t === 'occupying_owner') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400';
  return 'bg-muted text-muted-foreground';
};

export const UnitPeopleTab = ({ unitId }) => {
  const [data, setData] = useState(null);
  const [occupancy, setOccupancy] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!unitId) return;
    const fetchAuth = async (endpoint) => {
      const res = await fetch(`${API_URL}/api/v1${endpoint}`, { credentials: 'include' });
      if (res.ok) return res.json();
      return null;
    };
    const load = async () => {
      setLoading(true);
      const [relData, occData, rentalData] = await Promise.all([
        fetchAuth(`/contacts/unit/${unitId}/relationships`),
        fetchAuth(`/occupancy/units/${unitId}/occupancy-history`),
        fetchAuth(`/occupancy/units/${unitId}/rentals`),
      ]);
      setData(relData);
      setOccupancy(occData || []);
      setRentals(rentalData || []);
      setLoading(false);
    };
    load();
  }, [unitId]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  const current = data?.current || [];
  const history = data?.history || [];

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold text-foreground mb-2">Current Relationships</p>
        {current.length === 0 ? (
          <p className="text-xs text-muted-foreground py-3">No active relationships</p>
        ) : (
          <div className="space-y-2">
            {current.map(r => (
              <div key={r.relationship_id} className="p-3 border rounded-lg bg-muted/20" data-testid={`rel-current-${r.relationship_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <Badge className={`text-[10px] ${relColor(r.relationship_type)}`}>{relLabel(r.relationship_type)}</Badge>
                  <span className="text-[10px] text-muted-foreground">Since {new Date(r.active_from).toLocaleDateString()}</span>
                </div>
                <p className="text-sm font-medium">{r.contact_name || 'Unknown'}</p>
                <div className="flex gap-4 mt-1">
                  {r.contact_phone && <span className="text-xs text-muted-foreground">{r.contact_phone}</span>}
                  {r.contact_email && <span className="text-xs text-muted-foreground">{r.contact_email}</span>}
                </div>
                {r.notes && <p className="text-xs text-muted-foreground mt-1 italic">{r.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {occupancy.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-foreground mb-2">Occupancy History</p>
          <div className="space-y-1.5">
            {occupancy.map((o) => (
              <div key={o.occupancy_id} className="flex items-center justify-between text-xs py-2 px-3 bg-muted/30 rounded" data-testid={`occ-${o.occupancy_id}`}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] capitalize">{o.occupancy_type?.replace('_', ' ')}</Badge>
                  {o.contact_name && <span className="text-muted-foreground">{o.contact_name}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`text-[10px] ${o.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>{o.status}</Badge>
                  <span className="text-muted-foreground">{o.start_date ? new Date(o.start_date).toLocaleDateString() : ''}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {rentals.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-foreground mb-2">Rental Records</p>
          <div className="space-y-1.5">
            {rentals.map((r) => (
              <div key={r.rental_id} className="text-xs py-2 px-3 bg-muted/30 rounded" data-testid={`rental-${r.rental_id}`}>
                <div className="flex items-center justify-between">
                  <Badge className={`text-[10px] ${r.status === 'active' ? 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400' : 'bg-muted text-muted-foreground'}`}>{r.status}</Badge>
                  <span className="text-muted-foreground">{r.lease_start_date ? new Date(r.lease_start_date).toLocaleDateString() : ''}{r.lease_end_date ? ` — ${new Date(r.lease_end_date).toLocaleDateString()}` : ''}</span>
                </div>
                {r.monthly_rent && <p className="mt-1 text-muted-foreground">Rent: ₹{Number(r.monthly_rent).toLocaleString('en-IN')}/mo</p>}
                {r.agreement_reference && <p className="text-muted-foreground">Ref: {r.agreement_reference}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-foreground mb-2">Relationship History</p>
          <div className="space-y-1.5">
            {history.map(r => (
              <div key={r.relationship_id} className="flex items-center justify-between text-xs py-2 px-3 bg-muted/20 rounded opacity-70" data-testid={`rel-history-${r.relationship_id}`}>
                <div className="flex items-center gap-2">
                  <Badge className={`text-[10px] ${relColor(r.relationship_type)}`}>{relLabel(r.relationship_type)}</Badge>
                  <span>{r.contact_name || 'Unknown'}</span>
                  {r.contact_phone && <span className="text-muted-foreground">{r.contact_phone}</span>}
                </div>
                <span className="text-muted-foreground whitespace-nowrap">{new Date(r.active_from).toLocaleDateString()} — {r.active_until ? new Date(r.active_until).toLocaleDateString() : 'Present'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {current.length === 0 && history.length === 0 && occupancy.length === 0 && rentals.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">No people or transactions linked to this unit yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Relationships are created when you soft-block, book, or sell a unit.</p>
        </div>
      )}
    </div>
  );
};
